"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"

/**
 * The one action slot in the job header, filled by whichever view is open.
 *
 * Canvas 18a puts "New note" in the bar — "a quiet teal label in the bar, not a
 * filled pill floating in a header" — and the same slot is where the pipeline's
 * own add action belongs. The header cannot know what that action is, and the
 * view cannot reach the header, so the view registers a node here and the
 * header renders it.
 *
 * Outside a job route there is no slot; registering is then a no-op rather than
 * an error, so a view can be reused elsewhere without a provider.
 */

type Slot = {
  node: ReactNode
  setNode: (node: ReactNode) => void
  /** Overrides the bar's title while a view is doing something of its own. */
  title: string | null
  setTitle: (title: string | null) => void
}

const JobHeaderActionContext = createContext<Slot | null>(null)

export function JobHeaderActionProvider({ children }: { children: ReactNode }) {
  const [node, setNode] = useState<ReactNode>(null)
  const [title, setTitle] = useState<string | null>(null)
  const value = useMemo(() => ({ node, setNode, title, setTitle }), [node, title])
  return (
    <JobHeaderActionContext.Provider value={value}>
      {children}
    </JobHeaderActionContext.Provider>
  )
}

/** What the header should draw. */
export function useJobHeaderAction() {
  const slot = useContext(JobHeaderActionContext)
  return { action: slot?.node ?? null, title: slot?.title ?? null }
}

/**
 * Renames the bar for as long as the view says so — 18b's composer is titled
 * "New note", not "Notes", because that is what the screen now is.
 */
export function useJobHeaderTitle(title: string | null) {
  const setTitle = useContext(JobHeaderActionContext)?.setTitle

  useEffect(() => {
    setTitle?.(title)
    return () => setTitle?.(null)
  }, [title, setTitle])
}

/**
 * Fills the slot for as long as the view is mounted.
 *
 * `deps` is the usual effect dependency list: a `ReactNode` is a fresh object
 * every render, so passing the node alone would loop.
 */
export function useSetJobHeaderAction(build: () => ReactNode, deps: unknown[]) {
  const slot = useContext(JobHeaderActionContext)
  const setNode = slot?.setNode

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const node = useMemo(build, deps)

  const clear = useCallback(() => setNode?.(null), [setNode])

  useEffect(() => {
    setNode?.(node)
    return clear
  }, [node, setNode, clear])
}
